import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { BASE_URL } from "../../const";

interface Company {
  tenant_id: string;
  name: string;
  created_at: string;
}

/**
 * Decode the current session JWT to extract the tenant_id.
 * Login-as partner usernames follow: partner+{tenant_id}@partner.asktally.com
 */
function getCurrentTenantId(): string | null {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const sub: string = payload.sub ?? "";
    const match = sub.match(/^partner\+(.+)@partner\.asktally\.com$/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Create a standalone axios instance for partner API calls.
 * This avoids the global response interceptor which dispatches logout() on 401.
 */
function createPartnerAxios() {
  const partnerToken = localStorage.getItem("partnerToken");
  return axios.create({
    baseURL: BASE_URL,
    headers: {
      "Content-Type": "application/json",
      ...(partnerToken ? { Authorization: `Bearer ${partnerToken}` } : {}),
    },
  });
}

const PartnerCompanySwitcher: React.FC = () => {
  const theme = useTheme();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [visible, setVisible] = useState(true);

  const currentTenantId = getCurrentTenantId();

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const partnerAxios = createPartnerAxios();
        const response = await partnerAxios.get("/v1/partner/companies/");
        setCompanies(response.data ?? []);
      } catch (error: any) {
        if (error?.response?.status === 401) {
          // Partner token expired — clean up and hide switcher
          localStorage.removeItem("partnerToken");
          setVisible(false);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchCompanies();
  }, []);

  const handleSwitch = async (tenantId: string) => {
    if (tenantId === currentTenantId || switching) return;
    setSwitching(true);
    try {
      const partnerAxios = createPartnerAxios();
      const response = await partnerAxios.get(
        `/v1/partner/companies/${tenantId}/login-as/`,
      );
      const url: string = response.data?.url;
      if (url) {
        const newToken = new URL(url).searchParams.get("token");
        if (newToken) {
          localStorage.setItem("token", newToken);
          window.location.reload();
          return;
        }
      }
    } catch {
      // Silently fail — user stays on current company
    }
    setSwitching(false);
  };

  if (!visible) return null;

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1} py="6px">
        <CircularProgress size={16} />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1 }}>
      <Select
        value={currentTenantId ?? ""}
        onChange={(e) => handleSwitch(e.target.value as string)}
        size="small"
        fullWidth
        disabled={switching}
        sx={{
          fontSize: "1rem",
          backgroundColor: "rgba(0, 0, 0, 0.16)",
          borderRadius: "14px",
          minHeight: "36px",
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "& .MuiSelect-select": {
            py: "8px",
            px: "14px",
            textAlign: "left",
          },
          "& .MuiSelect-icon": {
            color: theme.palette.common.black,
          },
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          PaperProps: {
            sx: {
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.text.primary,
              maxWidth: 260,
              mt: "-4px",
              border: `1px solid ${theme.palette.divider}`,
              padding: theme.spacing(1),
              borderRadius: theme.shape.borderRadius * 1.5,
              boxShadow: "0px 4px 10px rgba(0,0,0,0.25)",
              "& .MuiList-root": {
                padding: 0,
              },
            },
          },
          MenuListProps: {
            sx: {
              padding: 0,
            },
          },
        }}
      >
        {companies.map((company) => (
          <MenuItem
            key={company.tenant_id}
            value={company.tenant_id}
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.pxToRem(14),
              minHeight: 20,
              py: 1,
              borderRadius: theme.shape.borderRadius / 2,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.primary.main,
              },
            }}
          >
            {company.name}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

export default PartnerCompanySwitcher;
